import { useState } from 'react';
import { Banner, Button, DataTable, Field, Input, Modal, Pill, Td } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { fmtDate, money, todayISO } from '../../lib/format';
import type { TabProps } from './types';

export default function SuppliersTab({ job, reload }: TabProps) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({ supplierName: '', amount: '', purchasedDate: todayISO() });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/jobs/${job.id}/suppliers`, { ...draft, amount: Number(draft.amount) });
      setAdding(false);
      setDraft({ supplierName: '', amount: '', purchasedDate: todayISO() });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the supplier');
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (id: string) => {
    setError(null);
    try {
      await api.put(`/api/jobs/${job.id}/suppliers/${id}/paid`);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not mark the supplier paid');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">
          {job.unpaidSuppliers > 0
            ? `${job.unpaidSuppliers} unpaid — unconditional waivers stay locked until these clear.`
            : 'All supplier obligations are paid.'}
        </p>
        <Button onClick={() => setAdding(true)}>Add supplier</Button>
      </div>

      {error && <Banner tone="urgent">{error}</Banner>}

      <DataTable head={['Supplier', 'Amount', 'Purchased', 'Status', '']} empty="No supplier obligations recorded.">
        {job.suppliers.map((s) => (
          <tr key={s.id} className="border-b border-border/60">
            <Td className="font-medium">{s.supplierName}</Td>
            <Td>{money(s.amount)}</Td>
            <Td className="text-muted">{fmtDate(s.purchasedDate)}</Td>
            <Td>{s.paidAt ? <Pill tone="ok">Paid</Pill> : <Pill tone="watch">Unpaid</Pill>}</Td>
            <Td>
              {!s.paidAt && (
                <Button variant="secondary" onClick={() => void markPaid(s.id)} className="px-3 py-1 text-xs">
                  Mark paid
                </Button>
              )}
            </Td>
          </tr>
        ))}
      </DataTable>

      {adding && (
        <Modal title="Add supplier obligation" onClose={() => setAdding(false)}>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Supplier name">
              <Input required value={draft.supplierName} onChange={(e) => setDraft({ ...draft, supplierName: e.target.value })} />
            </Field>
            <Field label="Amount">
              <Input required type="number" min="0.01" step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
            </Field>
            <Field label="Purchased date">
              <Input required type="date" value={draft.purchasedDate} onChange={(e) => setDraft({ ...draft, purchasedDate: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setAdding(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Add supplier'}</Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
